import type { ApiClient } from '@hetivo/hetivo-lib-front-utils';
import { toPaginatedResponse } from '@hetivo/hetivo-lib-front-utils';
import type {
  DismissServiceReminderBody,
  HetivoApiResponse,
  HetivoPaginatedResponse,
  PaginatedResponse,
  SearchServiceRemindersCriteria,
  ServiceReminderListItem,
  ServiceReminderQueueFilters,
  ServiceReminderStats,
  SnoozeServiceReminderBody,
} from '@hetivo/hetivo-lib-types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Adds `key: value` to `body` only when `value` is a non-empty array. */
function addArrayFilter(body: Record<string, unknown>, key: string, value: readonly string[] | undefined): void {
  if (value && value.length > 0) {
    body[key] = value;
  }
}

/**
 * The filters of a request body: a trimmed search when it is not blank, and each id list only when it
 * holds something, so a cleared filter is never sent as an empty array.
 */
function toFilterBody(filters: ServiceReminderQueueFilters): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const search = filters.search?.trim();
  if (search) {
    body.search = search;
  }
  addArrayFilter(body, 'locationIds', filters.locationIds);
  addArrayFilter(body, 'servicePlanIds', filters.servicePlanIds);
  addArrayFilter(body, 'territoryIds', filters.territoryIds);
  addArrayFilter(body, 'vehicleGroupIds', filters.vehicleGroupIds);
  return body;
}

// ─── API Factory ──────────────────────────────────────────────────────────────

export interface ServiceReminderApi {
  /**
   * `POST /service-reminders/:id/dismiss`: ends the reminder's occurrence with a reason; the row comes back as
   * the next occurrence, with a new `version`. A row that changed meanwhile is a 409. Needs `service-plans:manage`.
   */
  dismissServiceReminder: (id: string, body: DismissServiceReminderBody) => Promise<ServiceReminderListItem>;
  /** `POST /service-reminders/stats`: the three tab counts under the same filters as a search. */
  getServiceReminderStats: (filters: ServiceReminderQueueFilters) => Promise<ServiceReminderStats>;
  /** `POST /service-reminders/search?page=&limit=`: one page of a tab of the fleet-wide queue. */
  searchServiceReminders: (
    criteria: SearchServiceRemindersCriteria,
    page: number,
    limit: number,
  ) => Promise<PaginatedResponse<ServiceReminderListItem>>;
  /**
   * `POST /service-reminders/:id/snooze`: puts a reminder off to a new date with a reason. The body carries the
   * `version` of the row the user saw, so a row that changed meanwhile is a 409. Needs `service-plans:manage`.
   */
  snoozeServiceReminder: (id: string, body: SnoozeServiceReminderBody) => Promise<ServiceReminderListItem>;
}

/** The Service reminders queue (SWOOP-281). The reads need the Maintenance module and `service-plans:list`; snooze and dismiss need `service-plans:manage`. */
export function createServiceReminderApi(client: ApiClient): ServiceReminderApi {
  return {
    dismissServiceReminder: async (id: string, body: DismissServiceReminderBody): Promise<ServiceReminderListItem> => {
      const response = await client.execute<HetivoApiResponse<ServiceReminderListItem>>(() =>
        client.instance.post(`/service-reminders/${id}/dismiss`, body),
      );
      if (!response.data) {
        throw new Error('dismissServiceReminder: unexpected empty response');
      }
      return response.data;
    },

    getServiceReminderStats: async (filters: ServiceReminderQueueFilters): Promise<ServiceReminderStats> => {
      const response = await client.execute<HetivoApiResponse<ServiceReminderStats>>(() =>
        client.instance.post('/service-reminders/stats', toFilterBody(filters)),
      );
      if (!response.data) {
        throw new Error('getServiceReminderStats: unexpected empty response');
      }
      return response.data;
    },

    searchServiceReminders: async (
      criteria: SearchServiceRemindersCriteria,
      page: number,
      limit: number,
    ): Promise<PaginatedResponse<ServiceReminderListItem>> => {
      const body = { ...toFilterBody(criteria), tab: criteria.tab };
      const response = await client.execute<HetivoApiResponse<HetivoPaginatedResponse<ServiceReminderListItem>>>(() =>
        client.instance.post('/service-reminders/search', body, { params: { limit, page } }),
      );
      if (!response.data) {
        throw new Error('searchServiceReminders: unexpected empty response');
      }
      return toPaginatedResponse(response.data);
    },

    snoozeServiceReminder: async (id: string, body: SnoozeServiceReminderBody): Promise<ServiceReminderListItem> => {
      const response = await client.execute<HetivoApiResponse<ServiceReminderListItem>>(() =>
        client.instance.post(`/service-reminders/${id}/snooze`, body),
      );
      if (!response.data) {
        throw new Error('snoozeServiceReminder: unexpected empty response');
      }
      return response.data;
    },
  };
}
